import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../layout/Layout';
import { consoleApi, climateApi, shutterApi, haEntityApi } from '../../services/api';
import wsService from '../../services/websocket';
import { isRoomControlsRelayEnabled } from '../../utils/txFeatureFlags';
import RoomControlsPanel from './RoomControlsPanel';
import './SimpleRoomControls.css';

// ─── Climate Panel ───────────────────────────────────────────────────────────

const HVAC_MODES = ['off', 'heat', 'cool', 'auto'];
const OCCUPANCY_STATES = ['occupied', 'unoccupied'];

// WebUI #100 — failure wording is the server's own sentence, never fixed
// copy. Two mutually exclusive states, never conflated:
//   a command error  → the API's message, VERBATIM (err.response body
//                      first, then the transport message as fallback);
//   an unread status → the honest "State unknown" sentence (the device
//                      status could not be read at all).
// The previous fixed "Command failed … please try again." discarded the
// server's `message` and gave wrong advice when RA/HA is unreachable
// (nothing about the device will accept a retry until the link is back) —
// the retry copy is dropped outright (UI-5/P-3, the rename drawer's
// verbatim rule). Pre-cutover interim: Core !673 / M010 §5.7 retires this
// Core-routed actuation path entirely and replaces the buttons with
// "controls available from the DA app on site"; the fixed copy goes then.
const serverMessage = (error, fallback) => (
  error?.response?.data?.error?.message
  || error?.response?.data?.message
  || error?.message
  || fallback
);
const stateUnknownMessage = (what) => `State unknown. We could not load the ${what}, so it cannot be shown.`;

// A device whose HA state is 'unavailable' or 'unknown' cannot be commanded —
// controls must be disabled and the reason shown (WebUI #100).
const UNAVAILABLE_STATES = ['unavailable', 'unknown'];
const isUnavailableState = (value) => typeof value === 'string' && UNAVAILABLE_STATES.includes(value.toLowerCase());

const ClimatePanel = ({ roomId }) => {
  const [climate, setClimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingTemp, setPendingTemp] = useState(null);

  const fetchClimate = useCallback(async () => {
    try {
      const res = await climateApi.getRoomClimate(roomId);
      const data = res.data?.data || res.data;
      setClimate(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch climate:', err);
      setError(stateUnknownMessage('climate status'));
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    fetchClimate();
    // Subscribe to real-time climate updates
    const unsub = wsService.subscribe('climate:update', (payload) => {
      if (payload?.room_id === roomId) {
        setClimate((prev) =>
          prev ? { ...prev, ...payload } : payload
        );
      }
    });
    return () => unsub && unsub();
  }, [roomId, fetchClimate]);

  const handleSetTemperature = async (temp) => {
    setPendingTemp(temp);
    setActionError(null);
    try {
      await climateApi.setTemperature(roomId, temp);
      // Refetch to get confirmed state
      await fetchClimate();
    } catch (err) {
      console.error('Failed to set temperature:', err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    } finally {
      setPendingTemp(null);
    }
  };

  const handleSetMode = async (mode) => {
    setActionError(null);
    try {
      await climateApi.setMode(roomId, mode);
      await fetchClimate();
    } catch (err) {
      console.error('Failed to set mode:', err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    }
  };

  const handleSetOccupancy = async (state) => {
    setActionError(null);
    try {
      await climateApi.setOccupancy(roomId, state);
      await fetchClimate();
    } catch (err) {
      console.error('Failed to set occupancy:', err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    }
  };

  if (loading) return <div className="control-panel loading">Loading climate data…</div>;
  if (error) return <div className="control-panel error" role="alert">{error}</div>;
  if (!climate) return null;

  // Availability + readings. The backend nests the HA climate entity at
  // `climate` ({ state, attributes }) — flat fields kept as legacy fallbacks.
  const haClimate = climate.climate || null;
  const haAttributes = haClimate?.attributes || {};
  const isUnavailable = isUnavailableState(haClimate?.state);
  const currentTemp = haAttributes.current_temperature ?? climate.current_temperature ?? climate.currentTemperature ?? '—';
  const targetTemp = pendingTemp ?? haAttributes.temperature ?? climate.temperature ?? climate.targetTemperature ?? 20;
  const currentMode = isUnavailable ? 'off' : (haClimate?.state ?? climate.hvac_mode ?? climate.mode ?? 'off');
  const occupancy = climate?.occupancy?.state ?? climate?.occupancy ?? 'unoccupied';

  return (
    <div className={`control-panel climate-panel${isUnavailable ? ' entity-unavailable' : ''}`}>
      <h3>🌡️ Climate</h3>
      {actionError && (
        <div className="command-error" role="alert">{actionError}</div>
      )}
      {isUnavailable && (
        <p className="entity-unavailable-msg" role="status">
          Climate device is unavailable, so its controls are disabled.
        </p>
      )}
      <div className="climate-grid">
        <div className="climate-reading">
          <span className="label">Current</span>
          <span className="value">{currentTemp}°C</span>
        </div>
        <div className="climate-control">
          <span className="label">Target</span>
          <div className="temp-control">
            <button
              className="temp-btn"
              onClick={() => handleSetTemperature(targetTemp - 0.5)}
              disabled={pendingTemp !== null || isUnavailable}
            >
              −
            </button>
            <span className="temp-value">{targetTemp}°C</span>
            <button
              className="temp-btn"
              onClick={() => handleSetTemperature(targetTemp + 0.5)}
              disabled={pendingTemp !== null || isUnavailable}
            >
              +
            </button>
          </div>
        </div>
        <div className="climate-control">
          <span className="label">Mode</span>
          <select
            value={currentMode}
            onChange={(e) => handleSetMode(e.target.value)}
            className="control-select"
            disabled={isUnavailable}
          >
            {HVAC_MODES.map((m) => (
              <option key={m} value={m}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="climate-control">
          <span className="label">Occupancy</span>
          <select
            value={occupancy}
            onChange={(e) => handleSetOccupancy(e.target.value)}
            className="control-select"
            disabled={isUnavailable}
          >
            {OCCUPANCY_STATES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// ─── Shutter Panel ───────────────────────────────────────────────────────────

const ShutterPanel = ({ shutterControl }) => {
  const [shutter, setShutter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [positionInput, setPositionInput] = useState(null);
  const [tiltInput, setTiltInput] = useState(null);
  const [actionPending, setActionPending] = useState(false);

  const shutterId = shutterControl.id;

  const fetchShutter = useCallback(async () => {
    try {
      const res = await shutterApi.getShutter(shutterId);
      const data = res.data?.data || res.data;
      setShutter(data);
      setPositionInput(data?.state?.position ?? 0);
      if (data?.state?.tilt_position != null) {
        setTiltInput(data.state.tilt_position);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch shutter:', err);
      setError(stateUnknownMessage('shutter status'));
    } finally {
      setLoading(false);
    }
  }, [shutterId]);

  useEffect(() => {
    fetchShutter();
    const unsub = wsService.subscribe('shutter:update', (payload) => {
      if (payload?.id === shutterId || payload?.shutter_id === shutterId) {
        fetchShutter();
      }
    });
    return () => unsub && unsub();
  }, [shutterId, fetchShutter]);

  const handleAction = async (action) => {
    setActionPending(true);
    setActionError(null);
    try {
      if (action === 'open') await shutterApi.open(shutterId);
      else if (action === 'close') await shutterApi.close(shutterId);
      else if (action === 'stop') await shutterApi.stop(shutterId);
      // Delay briefly to let HA process, then refetch
      setTimeout(() => fetchShutter(), 1500);
    } catch (err) {
      console.error(`Failed to ${action} shutter:`, err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    } finally {
      setActionPending(false);
    }
  };

  const handleSetPosition = async (pos) => {
    setActionPending(true);
    setActionError(null);
    try {
      await shutterApi.setPosition(shutterId, pos);
      setTimeout(() => fetchShutter(), 1500);
    } catch (err) {
      console.error('Failed to set shutter position:', err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    } finally {
      setActionPending(false);
    }
  };

  const handleSetTilt = async (tilt) => {
    setActionPending(true);
    setActionError(null);
    try {
      await shutterApi.setTilt(shutterId, tilt);
      setTimeout(() => fetchShutter(), 1500);
    } catch (err) {
      console.error('Failed to set shutter tilt:', err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    } finally {
      setActionPending(false);
    }
  };

  if (loading) return <div className="control-panel loading">Loading shutter data…</div>;
  if (error) return <div className="control-panel error" role="alert">{error}</div>;
  if (!shutter) return null;

  const state = shutter.state || {};
  const capabilities = shutter.capabilities || {};
  const isUnavailable = isUnavailableState(state.current);
  const stateLabel = state.is_opening
    ? 'Opening…'
    : state.is_closing
    ? 'Closing…'
    : state.current
    ? state.current.charAt(0).toUpperCase() + state.current.slice(1)
    : '—';

  return (
    <div className={`control-panel shutter-panel${isUnavailable ? ' entity-unavailable' : ''}`}>
      <h3>🪟 {shutterControl.name || 'Shutter'}</h3>
      {actionError && (
        <div className="command-error" role="alert">{actionError}</div>
      )}
      {isUnavailable && (
        <p className="entity-unavailable-msg" role="status">
          Shutter is unavailable, so its controls are disabled.
        </p>
      )}
      <div className="shutter-state">
        <span className={`state-badge state-${state.current || 'unknown'}`}>
          {stateLabel}
        </span>
        {state.position != null && (
          <span className="position-label">{state.position}%</span>
        )}
      </div>

      {/* Action buttons */}
      <div className="shutter-actions">
        {capabilities.open && (
          <button
            className="action-btn open-btn"
            onClick={() => handleAction('open')}
            disabled={actionPending || isUnavailable}
          >
            ▲ Open
          </button>
        )}
        {capabilities.stop && (
          <button
            className="action-btn stop-btn"
            onClick={() => handleAction('stop')}
            disabled={actionPending || isUnavailable}
          >
            ■ Stop
          </button>
        )}
        {capabilities.close && (
          <button
            className="action-btn close-btn"
            onClick={() => handleAction('close')}
            disabled={actionPending || isUnavailable}
          >
            ▼ Close
          </button>
        )}
      </div>

      {/* Position slider */}
      {capabilities.set_position && (
        <div className="slider-group">
          <label>Position</label>
          <div className="slider-row">
            <input
              type="range"
              min="0"
              max="100"
              value={positionInput ?? 0}
              onChange={(e) => setPositionInput(Number(e.target.value))}
              onMouseUp={() => handleSetPosition(positionInput)}
              onTouchEnd={() => handleSetPosition(positionInput)}
              disabled={actionPending || isUnavailable}
              className="slider"
            />
            <span className="slider-value">{positionInput ?? 0}%</span>
          </div>
        </div>
      )}

      {/* Tilt slider */}
      {capabilities.set_tilt_position && (
        <div className="slider-group">
          <label>Tilt</label>
          <div className="slider-row">
            <input
              type="range"
              min="0"
              max="100"
              value={tiltInput ?? 0}
              onChange={(e) => setTiltInput(Number(e.target.value))}
              onMouseUp={() => handleSetTilt(tiltInput)}
              onTouchEnd={() => handleSetTilt(tiltInput)}
              disabled={actionPending || isUnavailable}
              className="slider"
            />
            <span className="slider-value">{tiltInput ?? 0}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── HA Entity Panel (Switch & Light) ────────────────────────────────────────

const HaEntityPanel = ({ entityControl }) => {
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  const [brightnessInput, setBrightnessInput] = useState(null);
  const [colorTempInput, setColorTempInput] = useState(null);

  const entityId = entityControl.id;
  const haClass = entityControl.ha_class;
  const isLight = haClass === 'light';

  // Determine capabilities from cached supported_color_modes (no extra API call)
  const cachedModes = entityControl.supported_color_modes || [];
  const cachedSupportsBrightness = isLight && cachedModes.length > 0 && !cachedModes.every(m => m === 'onoff');
  const cachedSupportsColorTemp = isLight && cachedModes.includes('color_temp');
  const cachedSupportsEffect = isLight && !!(entityControl.supported_features & 1);

  const fetchEntity = useCallback(async () => {
    try {
      const res = await haEntityApi.getEntity(entityId);
      const data = res.data?.data || res.data;
      setEntity(data);
      if (data?.state?.brightness_pct != null) {
        setBrightnessInput(data.state.brightness_pct);
      }
      if (data?.state?.color_temp_kelvin != null) {
        setColorTempInput(data.state.color_temp_kelvin);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch entity:', err);
      setError(stateUnknownMessage('device status'));
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    fetchEntity();
    const unsub = wsService.subscribe('ha_entity:update', (payload) => {
      if (payload?.id === entityId) fetchEntity();
    });
    return () => unsub && unsub();
  }, [entityId, fetchEntity]);

  const handleAction = async (action) => {
    setActionPending(true);
    setActionError(null);
    try {
      if (action === 'on') await haEntityApi.turnOn(entityId);
      else if (action === 'off') await haEntityApi.turnOff(entityId);
      else if (action === 'toggle') await haEntityApi.toggle(entityId);
      setTimeout(() => fetchEntity(), 1000);
    } catch (err) {
      console.error(`Failed to ${action} entity:`, err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    } finally {
      setActionPending(false);
    }
  };

  const handleSetBrightness = async (pct) => {
    setActionPending(true);
    setActionError(null);
    try {
      await haEntityApi.setBrightness(entityId, { brightness_pct: pct });
      setTimeout(() => fetchEntity(), 1000);
    } catch (err) {
      console.error('Failed to set brightness:', err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    } finally {
      setActionPending(false);
    }
  };

  const handleSetColorTemp = async (kelvin) => {
    setActionPending(true);
    setActionError(null);
    try {
      await haEntityApi.setColour(entityId, { color_temp_kelvin: kelvin });
      setTimeout(() => fetchEntity(), 1000);
    } catch (err) {
      console.error('Failed to set colour temp:', err);
      setActionError(serverMessage(err, 'The command could not be completed.'));
    } finally {
      setActionPending(false);
    }
  };

  if (loading) return <div className="control-panel loading">Loading device…</div>;
  if (error) return <div className="control-panel error" role="alert">{error}</div>;

  const state = entity?.state || {};
  const caps = entity?.capabilities || {};
  const isOn = state.current === 'on';
  const isUnavailable = state.current === 'unavailable' || state.current === 'unknown';
  const stateLabel = state.current
    ? state.current.charAt(0).toUpperCase() + state.current.slice(1)
    : 'Unknown';
  const icon = isLight ? '💡' : '🔌';

  // Merge live capabilities with cached values (live takes precedence if available)
  const supportsBrightness = caps.supports_brightness ?? cachedSupportsBrightness;
  const supportsColorTemp = caps.supports_color_temp ?? cachedSupportsColorTemp;
  const supportsEffect = (entityControl.supported_features & 1) > 0;
  const minKelvin = caps.min_color_temp_kelvin || 2700;
  const maxKelvin = caps.max_color_temp_kelvin || 6500;

  return (
    <div className={`control-panel entity-panel${isUnavailable ? ' entity-unavailable' : ''}`}>
      <h3>
        {icon} {entityControl.name || 'Device'}
        <span className={`entity-class-badge entity-class-${haClass}`}>{haClass}</span>
      </h3>

      {/* State badge */}
      <div className="entity-state">
        <span className={`state-badge state-${state.current || 'unknown'}`}>
          {isOn ? '●' : '○'} {stateLabel}
        </span>
        {isOn && isLight && state.brightness_pct != null && (
          <span className="position-label">{state.brightness_pct}%</span>
        )}
      </div>

      {actionError && (
        <div className="command-error" role="alert">{actionError}</div>
      )}

      {/* On / Toggle / Off */}
      {!isUnavailable && (
        <div className="shutter-actions">
          <button
            className="action-btn open-btn"
            onClick={() => handleAction('on')}
            disabled={actionPending || isOn}
            title="Turn On"
          >
            ● On
          </button>
          <button
            className="action-btn stop-btn"
            onClick={() => handleAction('toggle')}
            disabled={actionPending}
            title="Toggle"
          >
            ⇄ Toggle
          </button>
          <button
            className="action-btn close-btn"
            onClick={() => handleAction('off')}
            disabled={actionPending || !isOn}
            title="Turn Off"
          >
            ○ Off
          </button>
        </div>
      )}

      {isUnavailable && (
        <p className="entity-unavailable-msg" role="status">
          Device is currently unavailable, so its controls are disabled.
        </p>
      )}

      {/* Brightness slider — light only, when on */}
      {isLight && supportsBrightness && isOn && (
        <div className="slider-group">
          <label>Brightness</label>
          <div className="slider-row">
            <input
              type="range"
              min="1"
              max="100"
              value={brightnessInput ?? state.brightness_pct ?? 100}
              onChange={(e) => setBrightnessInput(Number(e.target.value))}
              onMouseUp={() => handleSetBrightness(brightnessInput ?? state.brightness_pct ?? 100)}
              onTouchEnd={() => handleSetBrightness(brightnessInput ?? state.brightness_pct ?? 100)}
              disabled={actionPending}
              className="slider"
            />
            <span className="slider-value">{brightnessInput ?? state.brightness_pct ?? 100}%</span>
          </div>
        </div>
      )}

      {/* Colour temperature slider — light only, when on */}
      {isLight && supportsColorTemp && isOn && (
        <div className="slider-group">
          <label>Colour Temperature</label>
          <div className="color-temp-row">
            <span className="temp-icon">🕯</span>
            <input
              type="range"
              min={minKelvin}
              max={maxKelvin}
              value={colorTempInput ?? state.color_temp_kelvin ?? Math.round((minKelvin + maxKelvin) / 2)}
              onChange={(e) => setColorTempInput(Number(e.target.value))}
              onMouseUp={() => handleSetColorTemp(colorTempInput ?? state.color_temp_kelvin)}
              onTouchEnd={() => handleSetColorTemp(colorTempInput ?? state.color_temp_kelvin)}
              disabled={actionPending}
              className="slider color-temp-slider"
            />
            <span className="temp-icon">❄</span>
            <span className="slider-value">{colorTempInput ?? state.color_temp_kelvin ?? '—'}K</span>
          </div>
        </div>
      )}

      {/* Effect indicator — show currently active effect if any */}
      {isLight && supportsEffect && isOn && state.effect && (
        <div className="entity-effect">
          <span className="label">Effect</span>
          <span className="effect-name">✨ {state.effect}</span>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const SimpleRoomControls = () => {
  const { user, logout } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // M004-F0778 §2: rooms whose describe answered hidden keep the old ClimatePanel (flag on only).
  const [roomControlsHidden, setRoomControlsHidden] = useState({});

  // Fetch console rooms on mount
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await consoleApi.getMyRooms();
        const data = res.data?.data || res.data || [];
        const roomList = Array.isArray(data) ? data : [];
        setRooms(roomList);
        // Auto-select first room
        if (roomList.length > 0) {
          setSelectedRoomId(roomList[0].room_id);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to fetch console rooms:', err);
        // WebUI #100 (verbatim rule): the server's own refusal, never a
        // fixed "please try again later".
        setError(serverMessage(err, 'Unable to load rooms right now.'));
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
    // Connect WebSocket for real-time updates
    wsService.connect();
    return () => {
      // Don't disconnect WS on unmount — other components may use it
    };
  }, []);

  const selectedRoom = rooms.find((r) => r.room_id === selectedRoomId);
  const climateControl = selectedRoom?.controls?.find((c) => c.type === 'climate');
  const shutterControls = selectedRoom?.controls?.filter((c) => c.type === 'shutter') || [];
  const haEntityControls = selectedRoom?.controls?.filter((c) => c.type === 'ha_entity') || [];

  // Deduplicate rooms (a room may appear in multiple consoles)
  const uniqueRooms = rooms.reduce((acc, room) => {
    if (!acc.find((r) => r.room_id === room.room_id)) {
      acc.push(room);
    }
    return acc;
  }, []);

  const handleLogout = async () => {
    wsService.disconnect();
    await logout();
  };

  if (loading) {
    return (
      <Layout>
        <div className="room-controls-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading room controls…</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="room-controls-container">
        {/* Header */}
        <div className="rc-header">
          <h1>Room Controls</h1>
          <div className="rc-header-right">
            <span className="user-name">{user?.displayName || 'User'}</span>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="rc-error">
            <p>{error}</p>
          </div>
        )}

        {/* No rooms */}
        {!error && rooms.length === 0 && (
          <div className="rc-empty">
            <p>No rooms assigned to your account.</p>
            <p className="hint">Contact an administrator to get room access.</p>
          </div>
        )}

        {/* Room selector */}
        {uniqueRooms.length > 0 && (
          <div className="room-selector">
            <label htmlFor="room-select">Select Room</label>
            <select
              id="room-select"
              value={selectedRoomId || ''}
              onChange={(e) => setSelectedRoomId(Number(e.target.value))}
              className="room-select"
            >
              {uniqueRooms.map((room) => (
                <option key={room.room_id} value={room.room_id}>
                  {room.room_name}
                  {room.console_name ? ` — ${room.console_name}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Controls for selected room */}
        {selectedRoom && (
          <div className="controls-area">
            {/* Climate */}
            {/* M004-F0778 §2: with the flag on, the room-controls panel replaces ClimatePanel unless describe reported hidden. */}
            {climateControl && isRoomControlsRelayEnabled() && !roomControlsHidden[selectedRoom.room_id] && (
              <RoomControlsPanel
                key={selectedRoom.room_id}
                roomId={selectedRoom.room_id}
                onHidden={() => setRoomControlsHidden((prev) => ({ ...prev, [selectedRoom.room_id]: true }))}
              />
            )}
            {climateControl && (!isRoomControlsRelayEnabled() || roomControlsHidden[selectedRoom.room_id]) && <ClimatePanel roomId={selectedRoom.room_id} />}

            {/* Shutters */}
            {shutterControls.map((sc) => (
              <ShutterPanel key={sc.id} shutterControl={sc} />
            ))}

            {/* HA Entities (switches & lights) */}
            {haEntityControls.map((ec) => (
              <HaEntityPanel key={ec.id} entityControl={ec} />
            ))}

            {/* No controls */}
            {!climateControl && shutterControls.length === 0 && haEntityControls.length === 0 && (
              <div className="rc-empty">
                <p>No controls configured for this room.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SimpleRoomControls;
